/**
 * On-ground assignment (mirror backend):
 * Admin or the club manager sets onGroundAssignedUserIds on create and update.
 * Each id is the staff user's _id. Allowed roles: CLUB_OPERATOR, CLUB_EMPLOYEE,
 * and they must already belong to the event's club.
 */

export const ON_GROUND_STAFF_ROLES = ["CLUB_OPERATOR", "CLUB_EMPLOYEE"];

const ON_GROUND_ROLE_SET = new Set(ON_GROUND_STAFF_ROLES);

function normalizeAssignedIdEntry(entry) {
  if (entry == null || entry === "") return "";
  if (typeof entry === "string" || typeof entry === "number") {
    return String(entry).trim();
  }
  if (typeof entry === "object") {
    const oid = entry.$oid;
    if (typeof oid === "string") return oid.trim();
    const nested = entry._id ?? entry.id ?? entry.userId;
    if (nested != null && typeof nested === "object" && nested.$oid) {
      return String(nested.$oid).trim();
    }
    if (nested != null && nested !== "") return String(nested).trim();
    if (typeof entry.user === "string" && entry.user.trim()) {
      return entry.user.trim();
    }
    if (entry.user && typeof entry.user === "object") {
      const u = entry.user._id ?? entry.user.id;
      if (u != null) return String(u).trim();
    }
  }
  const asStr = String(entry).trim();
  if (asStr === "[object Object]") return "";
  return asStr;
}

function unwrapOnGroundAssignedRaw(ev) {
  if (!ev || typeof ev !== "object") return null;
  const candidates = [
    ev.onGroundAssignedUserIds,
    ev.on_ground_assigned_user_ids,
    ev.onGroundAssignedUsers,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    if (Array.isArray(v)) return v;
  }
  return null;
}

export function pickOnGroundAssignedUserIds(ev) {
  if (!ev || typeof ev !== "object") return [];
  const raw = unwrapOnGroundAssignedRaw(ev);
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map(normalizeAssignedIdEntry).filter(Boolean))];
}

export function normalizeOnGroundAssignedUserIds(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map(normalizeAssignedIdEntry).filter(Boolean))];
}

export function pickStaffRole(row) {
  if (!row || typeof row !== "object") return "";
  const user = row.user && typeof row.user === "object" ? row.user : null;
  return String(row.role ?? row.userRole ?? user?.role ?? user?.userRole ?? "")
    .trim()
    .toUpperCase();
}

/**
 * Employees list with no role is CLUB_EMPLOYEE.
 * Operators list with no role is CLUB_OPERATOR.
 * Any explicit role outside the two allowed roles is excluded.
 */
export function isOnGroundAssignableStaff(row, source) {
  const role = pickStaffRole(row);
  if (ON_GROUND_ROLE_SET.has(role)) return true;
  if (role) return false;
  return source === "employee" || source === "operator";
}

export function onGroundRoleForRow(row, source) {
  const role = pickStaffRole(row);
  if (ON_GROUND_ROLE_SET.has(role)) return role;
  if (source === "operator") return "CLUB_OPERATOR";
  return "CLUB_EMPLOYEE";
}
